/**
 * Expected fields for quick filter pattern (testcase default)
 */
const QUICK_FILTER_FIELDS = new Set(['title', 'key', 'description']);

/**
 * Entity-specific field sets for quick search (name/key-or-prefix/description semantics).
 * - testcase: title, key, description
 * - project: name, prefix (backend has no "key" field), description
 * - testgroup: name, key, description
 */
export const QUICK_FILTER_FIELDS_BY_ENTITY = {
  testcase: ['title', 'key', 'description'],
  project: ['name', 'prefix', 'description'],
  testgroup: ['name', 'key', 'description']
};

/**
 * Check if AST node matches quick filter pattern
 * Uses set equality for fields (order-independent)
 * 
 * @param {Object} ast - AST node to check
 * @returns {boolean} True if matches quick filter pattern
 */
export function matchesQuickFilterPattern(ast) {
  // Must be OR node with exactly 3 items
  if (ast.type !== 'or' || !ast.items || ast.items.length !== 3) {
    return false;
  }
  
  const seenFields = new Set();
  let value = null;
  
  // Check each item
  for (const item of ast.items) {
    // Must be predicate
    if (item.type !== 'predicate') {
      return false;
    }
    
    // Must use 'contains' operator
    if (item.op !== 'contains') {
      return false;
    }
    
    // All values must be the same
    if (value === null) {
      value = item.value;
    } else if (item.value !== value) {
      return false;
    }
    
    // Track field
    seenFields.add(item.field);
  }
  
  // Must have exactly the expected fields (set equality)
  return QUICK_FILTER_FIELDS.size === seenFields.size &&
         [...QUICK_FILTER_FIELDS].every(f => seenFields.has(f));
}

/**
 * Create quick filter AST pattern (OR of contains over given fields).
 * 
 * @param {string} value - Quick filter value (used as-is; caller should trim)
 * @param {string[]} [fields] - Field names to OR together. Default: ['title', 'key', 'description'] (testcase)
 * @returns {Object} Quick filter AST
 */
export function createQuickFilterAST(value, fields = ['title', 'key', 'description']) {
  const fieldList = Array.isArray(fields) && fields.length > 0 ? fields : ['title', 'key', 'description'];
  return {
    type: 'or',
    items: fieldList.map(field => ({
      type: 'predicate',
      field,
      op: 'contains',
      value
    }))
  };
}

